import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Scroll handling for client-side navigation.
 *
 * Replaces Next's router scrolling plus the `data-scroll-behavior="smooth"`
 * attribute that layout.tsx needed to work around it. The rule is the same one
 * that attribute existed to enforce:
 *
 *   - A route change jumps to the top instantly. `scroll-smooth` is set on
 *     <html> for in-page anchors, and letting a route change animate through it
 *     means the browser is still mid-scroll when the next paint measures the
 *     position -- which is how every cross-page navigation used to land 125px
 *     down, the height of the announcement bar plus the nav.
 *   - A hash animates, because that is the anchor case the smooth scrolling is
 *     there for. Targets carry `scroll-mt-[92px]` to clear the sticky nav.
 */
export function ScrollBehavior() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      /* decodeURIComponent so a non-ASCII id still resolves; querySelector
         would otherwise be handed a percent-encoded selector. */
      const id = decodeURIComponent(hash.slice(1));
      const target = document.getElementById(id);

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}
